/* Horizontal filter chips (§7 — filtering is always horizontal chips).

   Lives in component/ rather than in a workspace because Index filters by kind
   and Library by category with the identical control. It was written for Index
   first and moved here when the second caller appeared, rather than copied — a
   fourth independently-drifted copy of a shared pattern is exactly what ADR 0003
   decisions 11-13 had to clean up.

   Purely presentational: the caller owns the selection and computes the counts.
   The "All" chip is not special-cased here — it is a row with an empty `value`. */

import { LitElement, html, css, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { shadowReset } from '../../design/base.js';
import '../g-icon/g-icon.js';

/** One chip. `value` is what `select` reports; empty string is the All chip. */
export interface ChipOption {
  value: string;
  label: string;
  n: number;
  /**
   * Optional g-icon name, rendered before the label.
   *
   * Both optional fields exist for Map, where a chip filters by place kind and
   * the kind's icon and hue are what tie it to its pins, its list rows and its
   * card. Index (14 mention kinds) and Library (free-form categories) have no
   * icon or colour to give, so they simply omit them and render exactly as
   * before — which is why this is two optional fields rather than a variant.
   */
  icon?: string;
  /**
   * Optional CSS colour — a token reference, e.g. `var(--k-food)`.
   *
   * Becomes the chip's whole accent, not just the icon's: hover border and the
   * selected border, tint and label all derive from it. Omit it and the chip
   * uses the system accent exactly as before.
   */
  color?: string;
}

@customElement('g-chips')
export class GChips extends LitElement {
  static override styles = [
    shadowReset,
    css`
      /* One row, scrolled horizontally, rather than wrapping to two. Keeps the
         filter bar a fixed height so sticky headers below it don't shift when the
         chip set changes with the search.

         The scrollbar is hidden deliberately: a chip clipped by the right edge is
         the affordance, and it never lies — it appears exactly when there is more
         to reach. Scrolling still works by wheel, trackpad and touch, and tabbing
         to an off-screen chip scrolls it into view, so nothing becomes
         unreachable without the bar. */
      :host {
        display: flex;
        flex-wrap: nowrap;
        gap: var(--space-2);
        overflow-x: auto;
        overscroll-behavior-x: contain;
        scrollbar-width: none;
      }
      :host::-webkit-scrollbar {
        display: none;
      }
      button {
        /* The chip's own accent. Falls back to the system accent, so a caller
           that passes no colour (Index, Library) is completely unaffected; a
           caller that does passes it once and it drives the icon, the hover
           border and the whole selected state together. */
        --tone: var(--chip-tone, var(--accent));
        /* never shrink to fit — the row scrolls instead */
        flex: none;
        position: relative;
        display: inline-flex;
        align-items: baseline;
        gap: var(--space-2);
        padding: 5px var(--space-3);
        border: 1px solid var(--border);
        border-radius: var(--radius-full);
        /* Held in a property rather than set directly so the press rule below can
           mix over whichever base is in force — the chip has two (plain and the
           selected tint), and a flat background: var(--press) would erase the
           second one at exactly the moment you are pressing it. */
        --chip-bg: var(--surface);
        background: var(--chip-bg);
        color: var(--text-dim);
        font-family: var(--font-ui);
        font-size: 0.82rem;
        cursor: pointer;
        white-space: nowrap;
        transition: background-color 120ms ease, color 120ms ease, border-color 120ms ease;
      }
      /* Guarded, because a touchscreen has no pointer-leave to end a hover: the
         chip you last touched keeps this border until something else takes it.
         Measured on the device — a long-pressed Food chip still carried its coral
         outline minutes later, through a query clear and a full re-render, while
         aria-pressed was false and box-shadow was none. Two chips looked outlined
         and only one of them meant anything, which reads as a stray focus ring
         rather than as hover.
         The guard is the app's idiom (base.ts, index-row, place-detail,
         settings/row); the chips were the outlier. */
      @media (hover: hover) {
        button:hover {
          border-color: var(--tone);
          color: var(--text);
        }
      }
      /* Tinted into --surface, not into transparent: the chip keeps the opaque
         base the unpressed rule above already gives it. Mixed into transparent
         the selected chip is 84% see-through, which reads as a tint only while
         something opaque sits behind it — over Map's basemap it is mostly map. */
      button[aria-pressed='true'] {
        border-color: var(--tone);
        --chip-bg: color-mix(in oklab, var(--tone) 16%, var(--surface));
        color: var(--accent);
        font-weight: 700;
      }
      /* After the selected rule, so it wins for a pressed chip too. --press is
         ink over transparent, so mixing it into --chip-bg keeps the pill opaque —
         it sits over Map's basemap, where a see-through chip is mostly map. */
      button:active {
        background: color-mix(in oklab, var(--text) 10%, var(--chip-bg));
      }
      /* ADR 0007 principle 8, applied here at last — the sweep measured every
         chip row in the app at ~28px tall.

         36px of paint, 44px of hit area. Material's own filter chip is 32dp and
         does NOT grow on touch; it meets the touch floor with an invisible
         extension, and Google Maps' map chips sit at the top of that range at
         ~36. The first version of this rule grew the real height to 44 instead
         and the chips read as thick — a filter row should not be the heaviest
         thing in Map's peek sheet.

         36 + 4 + 4 lands exactly on 44, so neighbouring extensions MEET across
         the 8px gap without overlapping. At Material's 32 they would overlap by
         4px and the sliver between two chips would activate whichever is later
         in DOM order, with nothing on screen to explain it.

         Narrow AND coarse, not coarse alone: map-panel overrides the nowrap
         above, so a touchscreen laptop would extend hit areas across two
         WRAPPED rows and they would overlap invisibly between them. Map's
         mobile sheet scrolls a single row (ADR 0012 decision 3), which is what
         makes the extension safe below 960px.

         align-items flips to center because the baseline alignment that suits a
         28px chip leaves the label riding high in a taller one. */
      @media (pointer: coarse) and (max-width: 959px) {
        /* The extension lives in the host's own padding, and this is not
           optional. The host scrolls horizontally, and overflow-x: auto forces
           overflow-y to auto as well — a scroll container clips at its padding
           box, so without this the 4px would be silently cut off and the chip
           would take taps across 36px while measuring 44. Verified on the
           device: the first build had exactly that bug, and every box measured
           correct while the hit test failed 3px above the chip.

           A caller must therefore not set the padding SHORTHAND on g-chips —
           it would reset padding-block and re-clip. Use padding-inline. */
        :host {
          padding-block: 4px;
        }
        button {
          align-items: center;
          min-height: 36px;
          padding: 5px var(--space-4);
        }
        button::after {
          content: '';
          position: absolute;
          inset: -4px 0;
        }
      }
      /* A chip carrying a caller's colour keeps --text as its label.
         The hue is mid-tone by design — it has to read on either surface — and
         mid-tone is exactly what fails as small bold text on a tint of itself:
         measured, the coral Food label came out at 3.6:1 on the light theme,
         under AA for 13px bold (§23). Against --text it is ~12:1 light and
         ~8.6:1 dark. Nothing is lost, because the border, the tint and the icon
         are all still the kind's hue — three signals to the label's one. */
      button.toned[aria-pressed='true'] {
        color: var(--text);
      }
      .n {
        font-family: var(--font-mono);
        font-size: 0.72rem;
        opacity: 0.75;
      }
      /* align-self, because the button aligns its children on the baseline and an
         icon has none — without this it sits low against the label and the count.

         15px against a 0.82rem (~13px) label: the icon is the fastest thing to
         recognise in the chip and reads as the chip's subject, so it carries a
         little more weight than the word beside it. */
      g-icon {
        align-self: center;
        --icon-size: 15px;
        color: var(--tone);
      }
      /* The icon keeps the full hue while the label is mixed for contrast — a
         glyph is a shape before it is a colour, so it can hold the pure value
         at a size where text could not. */
    `,
  ];

  @property({ attribute: false }) options: ChipOption[] = [];
  @property() selected = '';

  private pick(value: string): void {
    this.dispatchEvent(new CustomEvent('select', { detail: value, bubbles: true, composed: true }));
  }

  override render() {
    return html`
      ${this.options.map(
        (o) => html`
          <button
            class=${o.color ? 'toned' : nothing}
            aria-pressed=${o.value === this.selected ? 'true' : 'false'}
            style=${o.color ? `--chip-tone: ${o.color}` : nothing}
            @click=${() => this.pick(o.value)}
          >
            ${o.icon ? html`<g-icon name=${o.icon}></g-icon>` : nothing}${o.label}<span class="n"
              >${o.n.toLocaleString()}</span
            >
          </button>
        `,
      )}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'g-chips': GChips;
  }
}
